import type {
	Article,
	ArticleSummary,
	ShopifyProduct,
} from "@/lib/shopify/types";
import { siteConfig } from "./config";
import { toPlainText } from "./metadata";

export interface BreadcrumbItem {
	name: string;
	url: string;
}

export interface FAQItem {
	question: string;
	answer: string;
}

export interface ReviewData {
	author: string;
	rating: number;
	reviewBody: string;
	datePublished?: string;
}

/**
 * Site-wide aggregate rating (219 verified reviews, 4.9/5). Applied to
 * brand-level schemas (Organization, LocalBusiness). Product pages use
 * real per-product review data instead when available.
 */
export const siteAggregateRating = {
	"@type": "AggregateRating",
	ratingValue: "4.9",
	reviewCount: 219,
	bestRating: 5,
} as const;

export function generateOrganizationSchema() {
	return {
		"@context": "https://schema.org",
		"@type": "Organization",
		"@id": `${siteConfig.url}/#organization`,
		name: siteConfig.business.name,
		legalName: siteConfig.business.legalName,
		url: siteConfig.url,
		logo: {
			"@type": "ImageObject",
			url: `${siteConfig.url}${siteConfig.images.logo}`,
			width: 512,
			height: 512,
		},
		image: `${siteConfig.url}${siteConfig.images.logo}`,
		description: siteConfig.description,
		foundingDate: siteConfig.business.foundingDate,
		email: siteConfig.business.email,
		telephone: siteConfig.business.phone,
		address: {
			"@type": "PostalAddress",
			streetAddress: siteConfig.business.address.streetAddress,
			addressLocality: siteConfig.business.address.addressLocality,
			addressRegion: siteConfig.business.address.addressRegion,
			postalCode: siteConfig.business.address.postalCode,
			addressCountry: siteConfig.business.address.addressCountry,
		},
		geo: {
			"@type": "GeoCoordinates",
			latitude: siteConfig.business.geo.latitude,
			longitude: siteConfig.business.geo.longitude,
		},
		sameAs: siteConfig.business.sameAs,
		contactPoint: {
			"@type": "ContactPoint",
			telephone: siteConfig.business.phone,
			contactType: "customer service",
			email: siteConfig.business.email,
			availableLanguage: "English",
		},
	};
}

export function generateWebsiteSchema() {
	return {
		"@context": "https://schema.org",
		"@type": "WebSite",
		"@id": `${siteConfig.url}/#website`,
		name: siteConfig.name,
		url: siteConfig.url,
		description: siteConfig.description,
		publisher: {
			"@id": `${siteConfig.url}/#organization`,
		},
		potentialAction: {
			"@type": "SearchAction",
			target: {
				"@type": "EntryPoint",
				urlTemplate: `${siteConfig.url}/search?q={search_term_string}`,
			},
			"query-input": "required name=search_term_string",
		},
	};
}

export function generateLocalBusinessSchema() {
	return {
		"@context": "https://schema.org",
		"@type": "FoodEstablishment",
		"@id": `${siteConfig.url}/#localbusiness`,
		name: siteConfig.business.name,
		image: `${siteConfig.url}${siteConfig.images.logo}`,
		url: siteConfig.url,
		telephone: siteConfig.business.phone,
		email: siteConfig.business.email,
		priceRange: siteConfig.business.priceRange,
		address: {
			"@type": "PostalAddress",
			streetAddress: siteConfig.business.address.streetAddress,
			addressLocality: siteConfig.business.address.addressLocality,
			addressRegion: siteConfig.business.address.addressRegion,
			postalCode: siteConfig.business.address.postalCode,
			addressCountry: siteConfig.business.address.addressCountry,
		},
		geo: {
			"@type": "GeoCoordinates",
			latitude: siteConfig.business.geo.latitude,
			longitude: siteConfig.business.geo.longitude,
		},
		openingHoursSpecification: {
			"@type": "OpeningHoursSpecification",
			dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday"],
			opens: "06:00",
			closes: "16:00",
		},
		sameAs: siteConfig.business.sameAs,
		servesCuisine: ["Paleo", "Healthy", "High Protein"],
		acceptsReservations: false,
		hasMenu: `${siteConfig.url}/build-a-box`,
		aggregateRating: { ...siteAggregateRating },
	};
}

export function generateProductSchema(
	product: ShopifyProduct,
	reviews?: ReviewData[],
) {
	const price = parseFloat(
		product.priceRange?.minVariantPrice?.amount ||
			product.variants[0]?.price?.amount ||
			"0",
	);
	const compareAtPrice = product.compareAtPriceRange?.minVariantPrice?.amount
		? parseFloat(product.compareAtPriceRange.minVariantPrice.amount)
		: null;
	const currency =
		product.priceRange?.minVariantPrice?.currencyCode ||
		product.variants[0]?.price?.currencyCode ||
		"USD";
	const image = product.images.edges[0]?.node;

	const schema: Record<string, unknown> = {
		"@context": "https://schema.org",
		"@type": "Product",
		"@id": `${siteConfig.url}/product/${product.handle}#product`,
		name: product.title,
		description:
			toPlainText(product.seo?.description, 500) ||
			toPlainText(product.description, 500) ||
			product.title,
		image: image?.url ? [image.url] : [siteConfig.images.ogDefault],
		url: `${siteConfig.url}/product/${product.handle}`,
		sku: product.id,
		mpn: product.id,
		brand: {
			"@type": "Brand",
			name: siteConfig.business.name,
		},
		offers: {
			"@type": "Offer",
			url: `${siteConfig.url}/product/${product.handle}`,
			priceCurrency: currency,
			price: price.toFixed(2),
			priceValidUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
				.toISOString()
				.split("T")[0],
			availability: product.availableForSale
				? "https://schema.org/InStock"
				: "https://schema.org/OutOfStock",
			seller: {
				"@type": "Organization",
				name: siteConfig.business.name,
			},
		},
		category: product.productType || "Food",
	};

	if (compareAtPrice && compareAtPrice > price) {
		(schema.offers as Record<string, unknown>).priceSpecification = {
			"@type": "PriceSpecification",
			price: price.toFixed(2),
			priceCurrency: currency,
		};
	}

	if (reviews && reviews.length > 0) {
		const avgRating =
			reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length;
		schema.aggregateRating = {
			"@type": "AggregateRating",
			ratingValue: avgRating.toFixed(1),
			reviewCount: reviews.length,
			bestRating: 5,
		};
		schema.review = reviews.map((review) => ({
			"@type": "Review",
			author: {
				"@type": "Person",
				name: review.author,
			},
			reviewRating: {
				"@type": "Rating",
				ratingValue: review.rating,
				bestRating: 5,
			},
			reviewBody: review.reviewBody,
			datePublished:
				review.datePublished || new Date().toISOString().split("T")[0],
		}));
	}

	if (
		product.productType?.toLowerCase().includes("meal") ||
		!product.productType
	) {
		schema.additionalProperty = [
			{
				"@type": "PropertyValue",
				name: "Diet",
				value: "Paleo-friendly",
			},
			{
				"@type": "PropertyValue",
				name: "Preparation",
				value: "Ready in minutes",
			},
		];
	}

	return schema;
}

export function generateProductListSchema(
	products: ShopifyProduct[],
	listName: string,
	listUrl: string,
) {
	return {
		"@context": "https://schema.org",
		"@type": "ItemList",
		name: listName,
		url: `${siteConfig.url}${listUrl}`,
		numberOfItems: products.length,
		itemListElement: products.map((product, index) => {
			const amount =
				product.priceRange?.minVariantPrice?.amount ||
				product.variants[0]?.price?.amount;
			const currency =
				product.priceRange?.minVariantPrice?.currencyCode ||
				product.variants[0]?.price?.currencyCode ||
				"USD";
			return {
				"@type": "ListItem",
				position: index + 1,
				item: {
					"@type": "Product",
					"@id": `${siteConfig.url}/product/${product.handle}#product`,
					url: `${siteConfig.url}/product/${product.handle}`,
					name: toPlainText(product.title, 200),
					image: product.images.edges[0]?.node?.url,
					...(amount && {
						offers: {
							"@type": "Offer",
							priceCurrency: currency,
							price: parseFloat(amount).toFixed(2),
							availability: product.availableForSale
								? "https://schema.org/InStock"
								: "https://schema.org/OutOfStock",
						},
					}),
				},
			};
		}),
	};
}

/**
 * ProductGroup for a single-product box configurator (FBB, Burritos) whose
 * variants are the selectable boxes.
 *
 * Covers every published variant and every product image, following the same
 * conventions as generateProductSchema (sku = store id, seller organisation,
 * per-variant availability). Variants are modelled as Product nodes under
 * hasVariant, each carrying its own Offer with the live variant price.
 */
export function generateBoxProductGroupSchema(
	product: ShopifyProduct,
	pagePath: string,
) {
	const images = product.images.edges
		.map((edge) => edge.node?.url)
		.filter((url): url is string => Boolean(url));

	const priceValidUntil = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
		.toISOString()
		.split("T")[0];

	return {
		"@context": "https://schema.org",
		"@type": "ProductGroup",
		"@id": `${siteConfig.url}${pagePath}#box`,
		name: product.title,
		description:
			toPlainText(product.seo?.description, 500) ||
			toPlainText(product.description, 500) ||
			product.title,
		url: `${siteConfig.url}${pagePath}`,
		image: images.length ? images : [siteConfig.images.ogDefault],
		brand: {
			"@type": "Brand",
			name: siteConfig.business.name,
		},
		aggregateRating: { ...siteAggregateRating },
		hasVariant: product.variants.map((variant) => ({
			"@type": "Product",
			"@id": `${siteConfig.url}${pagePath}#${variant.id.replace("gid://shopify/ProductVariant/", "")}`,
			name: variant.title,
			description: toPlainText(
				product.seo?.description || product.description,
				300,
			),
			sku: variant.id,
			mpn: variant.id,
			image: images.length ? images : [siteConfig.images.ogDefault],
			brand: {
				"@type": "Brand",
				name: siteConfig.business.name,
			},
			offers: {
				"@type": "Offer",
				url: `${siteConfig.url}${pagePath}`,
				priceCurrency: variant.price.currencyCode,
				price: Number.parseFloat(variant.price.amount).toFixed(2),
				priceValidUntil,
				availability: variant.availableForSale
					? "https://schema.org/InStock"
					: "https://schema.org/OutOfStock",
				seller: {
					"@type": "Organization",
					name: siteConfig.business.name,
				},
			},
		})),
	};
}

export function generateBreadcrumbSchema(items: BreadcrumbItem[]) {
	return {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		itemListElement: items.map((item, index) => ({
			"@type": "ListItem",
			position: index + 1,
			name: item.name,
			item: item.url.startsWith("http")
				? item.url
				: `${siteConfig.url}${item.url}`,
		})),
	};
}

export function generateArticleSchema(article: Article | ArticleSummary) {
	const fullArticle = article as Article;
	const image = article.image?.url || siteConfig.images.ogDefault;

	return {
		"@context": "https://schema.org",
		"@type": "Article",
		"@id": `${siteConfig.url}/blog/${article.handle}#article`,
		headline: article.title,
		description: article.excerpt || "",
		image: image,
		datePublished: article.publishedAt,
		dateModified: article.publishedAt,
		author: {
			"@type": "Person",
			name: article.author?.name || siteConfig.business.name,
		},
		publisher: {
			"@type": "Organization",
			name: siteConfig.business.name,
			logo: {
				"@type": "ImageObject",
				url: `${siteConfig.url}${siteConfig.images.logo}`,
			},
		},
		mainEntityOfPage: {
			"@type": "WebPage",
			"@id": `${siteConfig.url}/blog/${article.handle}`,
		},
		articleBody: fullArticle.content || "",
		wordCount: fullArticle.content
			? fullArticle.content.split(/\s+/).length
			: undefined,
		keywords: (fullArticle as Article).tags?.join(", ") || "",
	};
}

export function generateBlogSchema(articles: ArticleSummary[]) {
	return {
		"@context": "https://schema.org",
		"@type": "Blog",
		"@id": `${siteConfig.url}/blog#blog`,
		name: `${siteConfig.name} Blog`,
		description:
			"Nutrition tips, delicious recipes, and the latest news from Ice Age Meals.",
		url: `${siteConfig.url}/blog`,
		publisher: {
			"@type": "Organization",
			name: siteConfig.business.name,
			logo: {
				"@type": "ImageObject",
				url: `${siteConfig.url}${siteConfig.images.logo}`,
			},
		},
		blogPost: articles.slice(0, 10).map((article) => ({
			"@type": "BlogPosting",
			headline: article.title,
			url: `${siteConfig.url}/blog/${article.handle}`,
			datePublished: article.publishedAt,
			image: article.image?.url,
			author: {
				"@type": "Person",
				name: article.author?.name || siteConfig.business.name,
			},
		})),
	};
}

export function generateFAQSchema(faqs: FAQItem[]) {
	return {
		"@context": "https://schema.org",
		"@type": "FAQPage",
		mainEntity: faqs.map((faq) => ({
			"@type": "Question",
			name: faq.question,
			acceptedAnswer: {
				"@type": "Answer",
				text: faq.answer,
			},
		})),
	};
}

export function generateContactPageSchema() {
	return {
		"@context": "https://schema.org",
		"@type": "ContactPage",
		name: "Contact Ice Age Meals",
		description:
			"Get in touch with the Ice Age Meals team for questions about meals, orders, and delivery.",
		url: `${siteConfig.url}/contact`,
		mainEntity: {
			"@type": "Organization",
			name: siteConfig.business.name,
			telephone: siteConfig.business.phone,
			email: siteConfig.business.email,
			address: {
				"@type": "PostalAddress",
				streetAddress: siteConfig.business.address.streetAddress,
				addressLocality: siteConfig.business.address.addressLocality,
				addressRegion: siteConfig.business.address.addressRegion,
				postalCode: siteConfig.business.address.postalCode,
				addressCountry: siteConfig.business.address.addressCountry,
			},
		},
	};
}

export function generateAboutPageSchema() {
	return {
		"@context": "https://schema.org",
		"@type": "AboutPage",
		name: "About Ice Age Meals",
		description:
			"Learn about Ice Age Meals - our story, mission, and commitment to healthy, Paleo-friendly meals.",
		url: `${siteConfig.url}/about`,
		mainEntity: generateOrganizationSchema(),
	};
}

export function generateSearchResultsSchema(
	query: string,
	resultCount: number,
) {
	return {
		"@context": "https://schema.org",
		"@type": "SearchResultsPage",
		name: `Search results for "${query}"`,
		url: `${siteConfig.url}/search?q=${encodeURIComponent(query)}`,
		mainEntity: {
			"@type": "ItemList",
			numberOfItems: resultCount,
		},
	};
}

export function generateCollectionSchema(
	collectionName: string,
	collectionHandle: string,
	description: string,
	products: ShopifyProduct[],
) {
	return {
		"@context": "https://schema.org",
		"@type": "CollectionPage",
		name: collectionName,
		description: description,
		url: `${siteConfig.url}/build-a-box?collection=${collectionHandle}`,
		mainEntity: generateProductListSchema(
			products,
			collectionName,
			`/build-a-box?collection=${collectionHandle}`,
		),
	};
}

export function JsonLd({
	data,
}: {
	data: Record<string, unknown> | Record<string, unknown>[];
}) {
	return (
		<script
			type="application/ld+json"
			// biome-ignore lint/security/noDangerouslySetInnerHtml: JSON-LD structured data
			dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
		/>
	);
}

export function generateHomePageSchemas() {
	return [
		generateOrganizationSchema(),
		generateWebsiteSchema(),
		generateLocalBusinessSchema(),
	];
}
